import { MapPin, StickyNote } from 'lucide-react';
import { DatePicker } from '@/components/ui/DatePicker';
import { SkeletonLines } from '@/components/ui/Skeleton';

/**
 * One class written up on one day — a row of `daily_reports`, as this card
 * reads it.
 *
 * Carries the section and subject ids as well as their names: a section that
 * takes the same teacher for two subjects has two reports with the same
 * `className`, and a card that passed only names back would reopen whichever of
 * them happened to be found first.
 */
export interface ClassNoteItem {
  id: string;
  classSectionUuid: string;
  subjectUuid: string;
  date: string;
  className: string;
  subject: string;
  room: string;
  content: string;
  createdAt: string;
}

interface TeacherClassNotesCardProps {
  notesDateFilter: string;
  setNotesDateFilter: (date: string) => void;
  filteredNotesList: ClassNoteItem[];
  formattedNotesDate: string;
  isLoading?: boolean;
  onEditNote: (note: ClassNoteItem) => void;
}

export function TeacherClassNotesCard({
  notesDateFilter,
  setNotesDateFilter,
  filteredNotesList,
  formattedNotesDate,
  isLoading = false,
  onEditNote,
}: TeacherClassNotesCardProps) {
  return (
    <div className="card bg-surface border border-line rounded-card shadow-2xs">
      <div className="border-b border-line bg-surface-2 px-4 sm:px-5 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3">
        <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
          <span className="h-5 w-1.5 rounded-pill bg-amber-500 shrink-0" />
          <h2 className="text-sm sm:text-base font-bold text-ink tracking-tight flex items-center gap-1.5 sm:gap-2 truncate">
            <StickyNote className="h-4 w-4 sm:h-4.5 sm:w-4.5 text-amber-600 shrink-0" />
            <span>My Class Notes</span>
          </h2>
        </div>
        <div className="w-full sm:w-40 shrink-0">
          <DatePicker
            label="Filter notes by date"
            hideLabel
            size="sm"
            isClearable
            // Cleared, the card falls back to the overview's day rather than to
            // every day — so the empty box names that day, not "Any date".
            placeholder={formattedNotesDate}
            value={notesDateFilter}
            onChange={(e) => setNotesDateFilter(e.target.value)}
            className="w-full sm:w-40"
          />
        </div>
      </div>

      <div className="p-4 flex flex-col gap-3 max-h-[380px] overflow-y-auto">
        {isLoading ? (
          <SkeletonLines count={4} />
        ) : filteredNotesList.length === 0 ? (
          <div className="p-6 text-center text-ink-3 flex flex-col items-center gap-2">
            <StickyNote className="h-8 w-8 text-amber-500/40" />
            <p className="text-sm font-medium">
              No class notes recorded for {formattedNotesDate}.
            </p>
            <p className="text-xs text-ink-3">
              Click the note icon on any schedule period to add a note.
            </p>
          </div>
        ) : (
          filteredNotesList.map((note) => (
            <div
              key={note.id}
              className="p-3.5 rounded-[12px] border border-amber-500/20 bg-amber-500/5 flex flex-col gap-2 hover:border-amber-500/40 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-extrabold text-amber-700 dark:text-amber-400">
                  {note.subject} ({note.className})
                </span>
                <span className="text-2xs text-ink-3 font-semibold">{note.createdAt}</span>
              </div>
              <p className="text-xs text-ink font-medium leading-relaxed bg-surface/60 p-2.5 rounded-[8px] border border-line">
                "{note.content}"
              </p>
              <div className="flex items-center justify-between pt-1">
                <span className="text-2xs text-ink-3 font-medium flex items-center gap-1">
                  <MapPin className="h-3 w-3 text-amber-600" /> {note.room}
                </span>
                <button
                  type="button"
                  onClick={() => onEditNote(note)}
                  className="text-2xs font-bold text-amber-600 hover:underline"
                >
                  Edit
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
